import {
  FunnelIcon,
  MinusCircleIcon,
  ArrowUturnLeftIcon,
} from "@heroicons/react/24/outline";
import { useState, useMemo, useCallback } from "react";
import { ExpandableSearch } from "@/core/components/expandable-search";
import { FilterPill } from "@/core/components/filter-pill";
import { Button } from "@/core/components/ui/button";
import {
  IconSelect,
  IconSelectContent,
  IconSelectItem,
  IconSelectTrigger,
} from "@/core/components/ui/icon-select";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { sortDateDescending } from "@/core/utils/date";
import { multiFieldSearch } from "@/core/utils/multi-field-search";
import { cn } from "@/core/utils/utils";
import {
  SPIFF_RULE_EVENTS,
  SPIFF_RULE_EVENTS_LABELS,
  SPIFF_RULE_EVENTS_OPTIONS,
} from "@/spiff-rules/constants";
import { SpiffRuleEvent } from "@/spiff-rules/spiff-rule";
import { getOrderedSpiffRuleEvents } from "@/spiff-rules/utils";
import { SpiffEventBadge } from "@/spiffs/common/spiff-event-badge";
import { SPIFF_SIDEBAR_SECTIONS } from "@/spiffs/constants";
import { useSpiffSidebar } from "@/spiffs/context/spiff-sidebar-context";
import { ISpiff, ISpiffTrigger } from "@/spiffs/spiffs.d";
import { enrichSpiffForFiltering, FilterableSpiff } from "@/spiffs/utils";

const SPIFF_SEARCH_FIELDS = [
  "display_identification",
  "searchableCustomerName",
  "searchableEventType",
  "searchableDate",
  "searchableAmount",
];

interface SpiffItemsHeaderProps {
  hasDismiss?: boolean;
}

const SpiffItemsHeader = ({ hasDismiss = false }: SpiffItemsHeaderProps) => (
  <Stack direction="row" align="center">
    <Typography
      variant="subtitleW3"
      textColor="interactive"
      className={cn("w-[156px] min-w-[156px]", {
        "w-[130px] min-w-[130px]": hasDismiss,
      })}
    >
      Identification
    </Typography>
    <Typography
      variant="subtitleW3"
      textColor="interactive"
      className="w-[107px] min-w-[107px]"
    >
      Date
    </Typography>
    <Typography
      variant="subtitleW3"
      textColor="interactive"
      className="w-[80px] min-w-[80px]"
    >
      Amount
    </Typography>
  </Stack>
);

const SPIFF_EVENT_PILL_COLORS = {
  [SPIFF_RULE_EVENTS.signup]: "bg-badge-green",
  [SPIFF_RULE_EVENTS.referral_creation]: "bg-badge-blue",
  [SPIFF_RULE_EVENTS.referral_completion]: "bg-badge-purple",
  [SPIFF_RULE_EVENTS.five_star_review]: "bg-badge-gold",
  [SPIFF_RULE_EVENTS.yard_sign_photo]: "bg-badge-aqua",
  [SPIFF_RULE_EVENTS.pricebook_item_sale]: "bg-badge-orange",
  [SPIFF_RULE_EVENTS.tech_generated_lead]: "bg-badge-purple",
  [SPIFF_RULE_EVENTS.driving_score]: "bg-badge-red",
  [SPIFF_RULE_EVENTS.generic]: "bg-badge-gray",
} as const;

interface SpiffItemProps {
  spiff: FilterableSpiff;
  onDismissSpiff?: (spiffId: string) => void;
  onUndismissSpiff?: (spiffId: string) => void;
  onSpiffClick?: (spiff: ISpiff) => void;
  isLastItem?: boolean;
}

const SpiffItem = ({
  spiff,
  onDismissSpiff,
  onUndismissSpiff,
  onSpiffClick: externalOnSpiffClick,
  isLastItem = false,
}: SpiffItemProps) => {
  // Only use spiff sidebar if no external callback provided
  const spiffSidebarContext = externalOnSpiffClick ? null : useSpiffSidebar();
  const updateSidebar = spiffSidebarContext?.updateSidebar;
  const trigger = spiff.trigger as ISpiffTrigger;

  const hasDismiss = !!(onDismissSpiff || onUndismissSpiff);
  const isPhotoSpiff =
    spiff.trigger_event === SPIFF_RULE_EVENTS.yard_sign_photo;
  const isDismissed = !!spiff.dismissed_at;

  const handleSpiffClick = () => {
    if (isPhotoSpiff) {
      if (externalOnSpiffClick) {
        externalOnSpiffClick(spiff);
      } else if (updateSidebar) {
        updateSidebar({
          isOpen: true,
          section: SPIFF_SIDEBAR_SECTIONS.spiffPhoto,
          selectedItemId: spiff.id,
        });
      }
    }
  };

  const handleSpiffAction = useCallback(() => {
    if (isDismissed && onUndismissSpiff) {
      onUndismissSpiff(spiff.id);
    } else if (!isDismissed && onDismissSpiff) {
      onDismissSpiff(spiff.id);
    }
  }, [isDismissed, onDismissSpiff, onUndismissSpiff, spiff.id]);

  const contentSection = (
    <>
      <Stack
        gap="xs"
        className={cn("w-[156px] min-w-[156px]", {
          "w-[130px] min-w-[130px]": hasDismiss,
        })}
      >
        <Typography variant="bodyW1" className="line-clamp-2">
          {spiff.display_identification}
        </Typography>
        <SpiffEventBadge triggerEvent={spiff.trigger_event} size="sm" />
      </Stack>

      <Stack gap="xs" className="w-[107px] min-w-[107px]">
        <Typography variant="bodyW1">{spiff.searchableDate}</Typography>
        <Typography variant="captionW1" className="text-gray-500">
          {dayjs(
            trigger && "created_at" in trigger
              ? trigger.created_at
              : spiff.created_at,
          ).format("h:mm A")}
        </Typography>
      </Stack>

      <Typography variant="bodyW1" className="w-[80px] min-w-[80px]">
        {spiff.searchableAmount}
      </Typography>
    </>
  );

  return (
    <Stack
      direction="row"
      align="center"
      gap="md"
      className={cn("border-b border-b-stroke-tertiary pb-2", {
        "border-b-0": isLastItem,
        "pr-2": hasDismiss,
        "cursor-pointer": isPhotoSpiff,
      })}
      fullWidth
      onClick={handleSpiffClick}
    >
      {isDismissed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Stack
              direction="row"
              align="center"
              gap="md"
              className="opacity-50"
            >
              {contentSection}
            </Stack>
          </TooltipTrigger>
          <TooltipContent>
            This spiff is dismissed and needs to be reinstated
          </TooltipContent>
        </Tooltip>
      ) : (
        contentSection
      )}

      {hasDismiss && (
        <Tooltip>
          <TooltipTrigger>
            <Button
              variant="icon"
              size="iconSm"
              onClick={handleSpiffAction}
              className={cn({ "opacity-100": isDismissed })}
            >
              {isDismissed ? <ArrowUturnLeftIcon /> : <MinusCircleIcon />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isDismissed ? "Reinstate" : "Dismiss"} this spiff payout
          </TooltipContent>
        </Tooltip>
      )}
    </Stack>
  );
};

interface FilterableSpiffListProps {
  spiffs: ISpiff[];
  onDismissSpiff?: (spiffId: string) => void;
  onUndismissSpiff?: (spiffId: string) => void;
  onSpiffClick?: (spiff: ISpiff) => void;
  hideFilter?: boolean;
  emptyMessage?: string;
}

export const FilterableSpiffList = ({
  spiffs,
  onDismissSpiff,
  onUndismissSpiff,
  onSpiffClick,
  hideFilter = false,
  emptyMessage = "No spiff payouts",
}: FilterableSpiffListProps) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const filteredSpiffs = useMemo(() => {
    const enhancedSpiffs: FilterableSpiff[] = spiffs.map(
      enrichSpiffForFiltering,
    );

    let filtered = enhancedSpiffs;

    if (selectedFilters.length > 0) {
      filtered = filtered.filter((spiff) =>
        selectedFilters.includes(spiff.trigger_event),
      );
    }

    if (searchValue.trim()) {
      filtered = multiFieldSearch(filtered, searchValue, SPIFF_SEARCH_FIELDS);
    }

    // Sort by dismissed status, then by date (most recent first)
    return filtered.sort((a, b) => {
      const aDismissed = !!a.dismissed_at;
      const bDismissed = !!b.dismissed_at;

      if (aDismissed !== bDismissed) {
        return aDismissed ? 1 : -1;
      }

      return sortDateDescending(a.created_at, b.created_at);
    });
  }, [spiffs, selectedFilters, searchValue]);

  const availableEventTypes = useMemo(() => {
    const types = spiffs.map((spiff) => spiff.trigger_event);
    const uniqueTypes = Array.from(new Set(types)) as SpiffRuleEvent[];
    return getOrderedSpiffRuleEvents(uniqueTypes);
  }, [spiffs]);

  const handleFilterChange = (filters: string[]) => {
    if (filters.includes("all")) {
      setSelectedFilters([]);
      setIsPopoverOpen(false);
    } else {
      setSelectedFilters(filters);
    }
  };

  const handleRemoveFilter = (eventType: string) => {
    setSelectedFilters((prev) => prev.filter((f) => f !== eventType));
  };

  const hasDismiss = !!(onDismissSpiff || onUndismissSpiff);

  return (
    <Stack gap="sm" className="h-full min-h-0 overflow-hidden" fullWidth>
      {!hideFilter && (
        <div className="flex w-full items-start gap-2 pr-6">
          <div className="flex flex-1 flex-wrap items-center gap-1">
            {selectedFilters.map((eventType) => (
              <FilterPill
                key={eventType}
                backgroundColor={
                  SPIFF_EVENT_PILL_COLORS[eventType as SpiffRuleEvent] ||
                  "bg-gray-100 text-gray-800"
                }
                onClear={() => handleRemoveFilter(eventType)}
                className="whitespace-nowrap"
              >
                {SPIFF_RULE_EVENTS_LABELS[eventType as SpiffRuleEvent]}
              </FilterPill>
            ))}
          </div>

          <Stack direction="row" align="center" gap="sm">
            <ExpandableSearch
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search spiffs"
              buttonPosition="right"
            />
            <IconSelect
              value={selectedFilters}
              onValueChange={handleFilterChange}
              open={isPopoverOpen}
              onOpenChange={setIsPopoverOpen}
            >
              <IconSelectTrigger isActive={selectedFilters.length > 0}>
                <FunnelIcon className="size-4" />
              </IconSelectTrigger>
              <IconSelectContent align="end">
                <IconSelectItem value="all">All Types</IconSelectItem>
                {availableEventTypes.map((eventType) => (
                  <IconSelectItem key={eventType} value={eventType}>
                    {SPIFF_RULE_EVENTS_OPTIONS[eventType]}
                  </IconSelectItem>
                ))}
              </IconSelectContent>
            </IconSelect>
          </Stack>
        </div>
      )}

      <SpiffItemsHeader hasDismiss={hasDismiss} />

      {filteredSpiffs.length === 0 ? (
        <Typography
          variant="bodyW2"
          textColor="tertiary"
          className="mt-4 w-full text-center"
        >
          {spiffs.length === 0 ? emptyMessage : "No spiffs match your filters"}
        </Typography>
      ) : (
        <ScrollArea className="h-full w-full flex-1 pr-4">
          <Stack gap="sm" className="pb-4">
            {filteredSpiffs.map((spiff, index) => (
              <SpiffItem
                key={spiff.id}
                spiff={spiff}
                onDismissSpiff={onDismissSpiff}
                onUndismissSpiff={onUndismissSpiff}
                onSpiffClick={onSpiffClick}
                isLastItem={index === filteredSpiffs.length - 1}
              />
            ))}
          </Stack>
        </ScrollArea>
      )}
    </Stack>
  );
};
